import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Checkbox,
  Button,
  Typography,
  Popover,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

const ControlNameFilterPopover = ({ onFilterChange, activeFilter = false }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showRedFlagged, setShowRedFlagged] = useState(activeFilter);

  const open = Boolean(anchorEl);
  const isFilterActive = activeFilter;

  // Sync local state with activeFilter prop
  useEffect(() => {
    setShowRedFlagged(activeFilter);
  }, [activeFilter]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleRedFlaggedToggle = () => {
    setShowRedFlagged((prev) => !prev);
  };

  const handleApplyFilter = () => {
    onFilterChange(showRedFlagged);
    handleFilterClose();
  };

  const handleResetFilter = () => {
    setShowRedFlagged(false);
    onFilterChange(false);
    handleFilterClose();
  };

  const componentStyles = {
    popover: {
      width: "200px",
      maxHeight: "300px",
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[4],
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    headerText: {
      fontSize: "12px",
      fontWeight: 600,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    content: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.background.paper,
    },
    optionContainer: {
      display: "flex",
      alignItems: "center",
      padding: `${theme.spacing(0.5)} 0`,
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: theme.palette.grey[50],
      },
    },
    optionCheckbox: {
      marginRight: theme.spacing(1),
      color: theme.palette.grey[600],
      padding: theme.spacing(0.25),
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
    optionText: {
      fontSize: "12px",
      color: theme.palette.text.secondary,
      fontWeight: 400,
    },
    footer: {
      padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    buttonContainer: {
      display: "flex",
      gap: theme.spacing(0.75),
    },
    resetButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 500,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      minWidth: "auto",
      px: theme.spacing(1.5),
    },
    applyButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
      minWidth: "auto",
      px: theme.spacing(1.5),
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        sx={{
          ml: 0.5,
          padding: "4px",
          backgroundColor: isFilterActive
            ? theme.palette.grey[200]
            : "transparent",
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: isFilterActive
              ? theme.palette.grey[300]
              : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: componentStyles.popover }}
      >
        {/* HEADER */}
        <Box sx={componentStyles.header}>
          <Typography sx={componentStyles.headerText}>
            Filter Options
          </Typography>
        </Box>

        {/* CONTENT */}
        <Box sx={componentStyles.content}>
          <Box
            onClick={handleRedFlaggedToggle}
            sx={componentStyles.optionContainer}
          >
            <Checkbox
              size="small"
              checked={showRedFlagged}
              sx={componentStyles.optionCheckbox}
            />
            <Typography sx={componentStyles.optionText}>
              Red Flagged Controls
            </Typography>
          </Box>
        </Box>

        {/* FOOTER */}
        <Box sx={componentStyles.footer}>
          <Box sx={componentStyles.buttonContainer}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={componentStyles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={componentStyles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ControlNameFilterPopover;
